// MUI-free copy of Ui-components/our-services.
import {Fragment, memo} from 'react';
import type {CSSProperties, ReactNode} from 'react';
import Link from 'next/link';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import FadeInUp from '@/components/atoms/FadeInUp';
import styles from './OurServices.module.scss';

export interface OurServiceItem {
    icon: StaticImageData | string;
    heading?: string;
    /** '' renders the card without a link (adds `non-link-box`). */
    redirectLink?: string;
    body?: string;
}

interface OurServicesProps {
    ourServices?: OurServiceItem[];
    className?: string;
    heading?: ReactNode;
    subheading?: string;
    containerClass?: string;
    /** Was an Emotion `sx` on each card box; plain version applies it as an inline style. */
    xSx?: CSSProperties;
    /** Was an Emotion `sx` on the card grid container; plain version applies it as an inline style. */
    containerSx?: CSSProperties;
}

const renderOurServices = (data: OurServiceItem, className: string | undefined, xSx?: CSSProperties) => (
    <div
        className={
            data.redirectLink
                ? `${styles.card} ${className} card_main_div`
                : `${styles.card} ${className} card_main_div non-link-box ${styles.nonLink}`
        }
        style={xSx}
    >
        <div className={styles.headRow}>
            <div className={styles.iconBox}>
                <OptimizedImage src={data.icon} alt={data.heading ?? ''} width={60} height={60} priority={false} />
            </div>
            {data.redirectLink !== '' ? (
                <Link href={data?.redirectLink as string} target="_blank" className={styles.servicesLink}>
                    <h4 className={styles.cardHeading}>{data.heading}</h4>
                </Link>
            ) : (
                <h4 className={styles.cardHeading}>{data.heading}</h4>
            )}
        </div>

        <p className={styles.cardBody}>{data?.body ? parse(data.body) : ''}</p>
    </div>
);

const OurServices = ({ourServices, className, heading, subheading, containerClass, xSx, containerSx}: OurServicesProps) => (
    // `MuiGrid-item` kept on the root so a page that still wraps this in MUI `<Grid container spacing={2}>` keeps
    // applying its item padding; plain pages wrap it in OurServicesGrid instead.
    <div className={`MuiGrid-item ${styles.root}`}>
        <Container>
            <div>
                <div className="spectrum-header-wrapper solutions">
                    <div className={`spectrum-heading color-black our-service-main-heading text-align-left ${styles.heading}`}>
                        {heading}
                    </div>
                    <div className="divder-wrapper">
                        <hr className="divider-line divider-line-left" />
                    </div>
                    {subheading ? (
                        <div className="sub-heading-box">
                            <p className={`spectrum-sub-heading color-black spacing text-align-left ${styles.subheading}`}>
                                {subheading ? parse(subheading) : ''}
                            </p>
                        </div>
                    ) : null}
                </div>
                <div className={containerClass ? `${styles.grid} ${containerClass}` : styles.grid} style={containerSx}>
                    {ourServices?.map((data, index) => (
                        <Fragment key={index + 1}>
                            {/* The original `pt={subheading ? 4 : 1}` never applied: MUI's container `> .MuiGrid-item`
                                padding outranked it, so every item gets the plain 16px. */}
                            <div className={styles.item}>
                                <FadeInUp className="height-full">{renderOurServices(data, className, xSx)}</FadeInUp>
                            </div>
                        </Fragment>
                    ))}
                </div>
            </div>
        </Container>
    </div>
);

interface OurServicesGridProps {
    children: ReactNode;
    className?: string;
}

/** Plain stand-in for the page-owned `<Grid container spacing={2} className="our-service-defi-wrapper">`. */
export const OurServicesGrid = memo(({children, className}: OurServicesGridProps) => (
    <div className={className ? `${styles.pageGrid} ${className}` : styles.pageGrid}>{children}</div>
));
OurServicesGrid.displayName = 'OurServicesGrid';

export default memo(OurServices);
